import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Download component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconDownload extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.85449 16.7129C2.26871 16.7129 2.60449 17.0487 2.60449 17.4629V19.0237C2.60449 19.6526 2.85435 20.2558 3.2991 20.7006C3.74385 21.1453 4.34706 21.3952 4.97603 21.3952H19.023C19.6519 21.3952 20.2551 21.1453 20.6999 20.7006C21.1446 20.2558 21.3945 19.6526 21.3945 19.0237V17.4629C21.3945 17.0487 21.7303 16.7129 22.1445 16.7129C22.5587 16.7129 22.8945 17.0487 22.8945 17.4629V19.0237C22.8945 20.0505 22.4866 21.0352 21.7605 21.7613C21.0345 22.4873 20.0498 22.8952 19.023 22.8952H4.97603C3.94923 22.8952 2.96449 22.4873 2.23844 21.7613C1.51239 21.0352 1.10449 20.0505 1.10449 19.0237V17.4629C1.10449 17.0487 1.44028 16.7129 1.85449 16.7129Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M12.7498 1.85516C12.7498 1.44095 12.414 1.10516 11.9998 1.10516C11.5855 1.10516 11.2498 1.44095 11.2498 1.85516V13.875L7.88684 9.95156C7.61727 9.63706 7.1438 9.60064 6.8293 9.87021C6.51481 10.1398 6.47839 10.6132 6.74795 10.9277L11.4303 16.3904C11.5727 16.5567 11.7808 16.6523 11.9997 16.6523C12.2186 16.6523 12.4267 16.5567 12.5691 16.3904L17.2515 10.9277C17.521 10.6132 17.4846 10.1398 17.1701 9.87021C16.8556 9.60064 16.3821 9.63706 16.1126 9.95156L12.7498 13.8748V1.85516Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-download') === undefined) {
  customElements.define('cat-icon-download', BlocksIconDownload);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-download': BlocksIconDownload;
  }
}
